import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EnigmaActivity, QuizActivity, VisualAnalysisActivity } from "../components/TrainingKitActivities";
import api from "../config/axiosConfig";
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from "../contexts/ThemeContext";

const TrainingKitDetails = ({ activityId, onClose }) => {
  const { user } = useAuth();
  const { theme, darkMode } = useTheme();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [progress, setProgress] = useState({ status: "not started", score: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user._id) {
      toast.error("Utilisateur non authentifié.");
      navigate("/login");
      return;
    }

    const fetchActivityAndProgress = async () => {
      try {
        setLoading(true);
        if (!activityId || !/^[0-9a-fA-F]{24}$/.test(activityId)) {
          toast.error("ID d'activité invalide.");
          onClose ? onClose() : navigate("/dashboard");
          return;
        }

        const activityResponse = await api.get(`/training-activities/${activityId}`);
        setActivity(activityResponse.data);

        const progressResponse = await api.get(`/training-activities/progression/${activityId}`, {
          params: { userId: user._id },
        });
        setProgress(progressResponse.data || { status: "not started", score: 0 });
      } catch (error) {
        toast.error("Erreur de chargement.");
        onClose ? onClose() : navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchActivityAndProgress();
  }, [activityId, user?._id, navigate, onClose]); 

  const handleComplete = async (score = 0) => {
    try {
      await api.put(`/training-activities/progression/${activityId}`, {
        userId: user._id,
        status: "completed",
        score: activity.type === "quiz" ? score : 100,
        pesasEarned: activity.pesasReward || 0,
      });
      toast.success(`Activité ${activity.title} complétée ! +${activity.pesasReward} Pesas`);
      if (onClose) onClose();
      else navigate("/progressions");
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Erreur lors de la complétion de l'activité.";
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 8 }} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Activité non trouvée.</Typography>
        <Button
          onClick={() => (onClose ? onClose() : navigate("/dashboard"))}
          sx={{
            mt: 2,
            bgcolor: theme.palette.vibrantColors.collaboration,
            color: "#FFFFFF",
            borderRadius: 8,
          }}
        >
          Retour au tableau de bord
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        maxWidth: 800,
        mx: "auto",
        bgcolor: darkMode ? "#1E293B" : "#FFFFFF",
        borderRadius: 12,
        boxShadow: `0 4px 20px ${theme.palette.vibrantColors.collaboration}20`,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: darkMode ? "#E2E8F0" : "#1E3A8A",
          fontFamily: '"Poppins", sans-serif',
          mb: 2,
        }}
      >
        {activity.title}
      </Typography>
      <Typography
        sx={{
          color: darkMode ? "#FBBF24" : "#1E3A8A",
          mb: 2,
        }}
      >
        Statut : {progress.status} {activity.type === "quiz" && `| Score : ${progress.score}%`}
      </Typography>
      <Typography
        sx={{
          color: darkMode ? "#94A3B8" : "#6B7280",
          mb: 2,
        }}
      >
        {activity.description}
      </Typography>
      {activity.type === "quiz" && (
        <QuizActivity
          activity={activity}
          onComplete={handleComplete}
          learningStyle={user?.learningStyle || "visuel"}
        />
      )}
      {activity.type === "visual-analysis" && (
        <VisualAnalysisActivity
          activity={activity}
          onComplete={handleComplete}
          learningStyle={user?.learningStyle || "visuel"}
        />
      )}
      {activity.type === "enigma" && (
        <EnigmaActivity
          activity={activity}
          onComplete={handleComplete}
          learningStyle={user?.learningStyle || "visuel"}
        />
      )}
      
      <Button
        onClick={() => (onClose ? onClose() : navigate("/progressions"))}
        sx={{
          mt: 2,
          bgcolor: theme.palette.vibrantColors.collaboration,
          color: "#FFFFFF",
          borderRadius: 8,
        }}
      >
        Retour à la progression
      </Button>
    </Box>
  );
};

export default TrainingKitDetails;